import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../../../../shared/api/apiClient";
import { useTranslation } from "react-i18next";
import "./SourcesPage.css";

import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const normalize = (v) =>
  v === null || v === undefined ? "" : String(v).trim().toLowerCase();

const TELEGRAM_PRESET_KEY = "telegram";
const TELEGRAM_SOURCE_NAME = "Telegram Bot";

const isTelegramSourceName = (value) => {
  const name = normalize(value);
  return name === normalize(TELEGRAM_SOURCE_NAME) || name === "telegram";
};

const getPresetDisplayName = (preset, t) => t(preset.nameKey);

const getPresetPayloadName = (preset) => {
  if (preset.key === TELEGRAM_PRESET_KEY) return TELEGRAM_SOURCE_NAME;
  return preset.nameKey;
};

const getPresetPayloadDescription = (preset) => preset.descKey;

const defaultColumnFilters = {
  ui_id: "",
  name: "",
  description: "",
};

const DEFAULT_SOURCE_PRESETS = [
  { key: "website", nameKey: "source_preset_website", descKey: "source_preset_website_desc" },
  { key: "google_ads", nameKey: "source_preset_google_ads", descKey: "source_preset_google_ads_desc" },
  { key: "facebook_ads", nameKey: "source_preset_facebook_ads", descKey: "source_preset_facebook_ads_desc" },
  { key: "instagram", nameKey: "source_preset_instagram", descKey: "source_preset_instagram_desc" },
  { key: "telegram", nameKey: "source_preset_telegram", descKey: "source_preset_telegram_desc" },
  { key: "whatsapp", nameKey: "source_preset_whatsapp", descKey: "source_preset_whatsapp_desc" },
  { key: "referral", nameKey: "source_preset_referral", descKey: "source_preset_referral_desc" },
  { key: "cold_call", nameKey: "source_preset_cold_call", descKey: "source_preset_cold_call_desc" },
  { key: "walk_in", nameKey: "source_preset_walk_in", descKey: "source_preset_walk_in_desc" },
  { key: "exhibition", nameKey: "source_preset_exhibition", descKey: "source_preset_exhibition_desc" },
  { key: "business_card", nameKey: "source_preset_business_card", descKey: "source_preset_business_card_desc" },
  { key: "banner", nameKey: "source_preset_banner", descKey: "source_preset_banner_desc" },
  { key: "existing_client", nameKey: "source_preset_existing_client", descKey: "source_preset_existing_client_desc" },
  { key: "partners", nameKey: "source_preset_partners", descKey: "source_preset_partners_desc" },
  { key: "construction_site", nameKey: "source_preset_construction_site", descKey: "source_preset_construction_site_desc" },
  { key: "office_store", nameKey: "source_preset_office_store", descKey: "source_preset_office_store_desc" },
  { key: "client_calling", nameKey: "source_preset_client_calling", descKey: "source_preset_client_calling_desc" },
  { key: "friends", nameKey: "source_preset_friends", descKey: "source_preset_friends_desc" },
  { key: "word_of_mouth", nameKey: "source_preset_word_of_mouth", descKey: "source_preset_word_of_mouth_desc" },
  { key: "manual", nameKey: "source_preset_manual", descKey: "source_preset_manual_desc" },
];

const translateIfExists = (value, t, i18n) => {
  if (!value) return "";
  return i18n.exists(value) ? t(value) : value;
};

const tOr = (t, key, fallback) => {
  const value = t(key);
  return value === key ? fallback : value;
};

const isLockedSource = (src, telegramEnabled = false) => {
  if (!src) return false;
  if (src.is_system || src.locked_reason) return true;
  return telegramEnabled && isTelegramSourceName(src.name);
};

const getLockedReason = (src, telegramEnabled, t) => {
  if (src?.locked_reason) return src.locked_reason;
  if (telegramEnabled && isTelegramSourceName(src?.name)) {
    return tOr(t, "sources_telegram_locked", "Telegram Bot integration is enabled");
  }
  return tOr(t, "sources_system_locked", "System source is locked");
};

const SourcesRow = ({
  src,
  onEdit,
  onDelete,
  disabled,
  dragDisabled,
  isSearchingOrFiltering,
  openMenuId,
  setOpenMenuId,
  openMenuRef,
  saving,
  isLocked,
  lockedReason,
  t,
  i18n,
}) => {
  const actionBtnRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: src.id, disabled: dragDisabled || isLocked });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.75 : 1,
  };

  const isMenuOpen = openMenuId === src.id;

  const toggleMenu = () => {
    if (!actionBtnRef.current) return;

    const rect = actionBtnRef.current.getBoundingClientRect();
    const menuWidth = 220;
    const menuHeight = 104;

    const left = Math.max(
      8,
      Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8)
    );

    const top =
      rect.bottom + menuHeight > window.innerHeight
        ? rect.top - menuHeight - 6
        : rect.bottom + 6;

    setMenuPosition({ top, left });
    setOpenMenuId((prev) => (prev === src.id ? null : src.id));
  };

  return (
    <tr ref={setNodeRef} style={style} className={isDragging ? "table-active" : ""}>
      <td className="sources-col-drag">
        <button
          type="button"
          className="sources-drag-btn"
          title={
            isLocked
              ? lockedReason
              : dragDisabled
              ? isSearchingOrFiltering
                ? t("sources_clear_search_filters_to_reorder")
                : t("sources_reorder_disabled")
              : t("sources_drag_to_reorder")
          }
          {...attributes}
          {...listeners}
          disabled={disabled || dragDisabled || isLocked}
        >
          ☰
        </button>
      </td>

      <td className="sources-col-id">{src.ui_id ?? "-"}</td>

      <td>{translateIfExists(src.name, t, i18n)}</td>

      <td className="sources-description-cell">
        {translateIfExists(src.description, t, i18n) || "-"}
      </td>

      <td className="sources-col-actions">
        <button
          ref={actionBtnRef}
          type="button"
          className="sources-kebab-btn"
          onClick={toggleMenu}
          disabled={disabled}
        >
          ⋯
        </button>

        {isMenuOpen && (
          <div
            ref={openMenuRef}
            className="sources-actions-menu sources-actions-menu-fixed"
            style={{
              top: `${menuPosition.top}px`,
              left: `${menuPosition.left}px`,
            }}
          >
            <button
              type="button"
              className="sources-menu-item"
              disabled={saving || isLocked}
              title={isLocked ? lockedReason : undefined}
              onClick={() => {
                setOpenMenuId(null);
                onEdit(src);
              }}
            >
              <span className="sources-menu-icon sources-menu-icon-edit"><i className="lni lni-pencil-1"></i></span>
              {t("edit")}
            </button>

            <button
              type="button"
              className="sources-menu-item"
              disabled={saving || isLocked}
              title={isLocked ? lockedReason : undefined}
              onClick={() => onDelete(src)}
            >
              <span className="sources-menu-icon sources-menu-icon-delete"><i className="lni lni-trash-3"></i></span>
              {t("delete")}
            </button>
          </div>
        )}
      </td>
    </tr>
  );
};

const SourcesPage = () => {
  const { t, i18n } = useTranslation();

  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [error, setError] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [columnFilters, setColumnFilters] = useState(defaultColumnFilters);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "" });

  const [presetDropdownOpen, setPresetDropdownOpen] = useState(false);
  const [presetSearch, setPresetSearch] = useState("");
  const [selectedPresetKeys, setSelectedPresetKeys] = useState([]);

  const [openMenuId, setOpenMenuId] = useState(null);
  const [telegramEnabled, setTelegramEnabled] = useState(false);

  const openMenuRef = useRef(null);
  const presetDropdownRef = useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const fetchSources = async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await api.get("/sources", { params: { skip: 0, limit: 500 } });

      const list = (res.data || [])
        .slice()
        .sort((a, b) => (a.ui_id ?? 0) - (b.ui_id ?? 0));

      setSources(list);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.detail || t("error_loading_sources"));
    } finally {
      setLoading(false);
    }
  };

  const fetchTelegramSettings = async () => {
    try {
      const res = await api.get("/auth/telegram/settings");
      setTelegramEnabled(Boolean(res.data?.enabled));
    } catch (e) {
      console.warn("Failed to load Telegram settings", e);
      setTelegramEnabled(false);
    }
  };

  useEffect(() => {
    fetchSources();
    fetchTelegramSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onDown = (e) => {
      if (openMenuRef.current && !openMenuRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }

      if (
        presetDropdownRef.current &&
        !presetDropdownRef.current.contains(e.target)
      ) {
        setPresetDropdownOpen(false);
      }
    };

    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpenMenuId(null);
        setPresetDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);

    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const existingNamesSet = useMemo(() => {
    return new Set(sources.map((s) => normalize(s.name)));
  }, [sources]);

  const presetItems = useMemo(() => {
    return DEFAULT_SOURCE_PRESETS.map((p) => {
      const name = getPresetDisplayName(p, t);
      const payloadName = getPresetPayloadName(p);
      const description = t(p.descKey);

      const exists =
        existingNamesSet.has(normalize(name)) ||
        existingNamesSet.has(normalize(payloadName)) ||
        existingNamesSet.has(normalize(p.nameKey)) ||
        (p.key === TELEGRAM_PRESET_KEY &&
          (existingNamesSet.has(normalize(TELEGRAM_SOURCE_NAME)) ||
            existingNamesSet.has("telegram")));

      return {
        ...p,
        name,
        payloadName,
        description,
        exists,
      };
    });
  }, [existingNamesSet, t]);

  const availablePresetItems = useMemo(() => {
    const q = presetSearch.trim().toLowerCase();

    return presetItems.filter((p) => {
      if (!q) return true;

      return (
        normalize(p.name).includes(q) ||
        normalize(p.payloadName).includes(q) ||
        normalize(p.description).includes(q)
      );
    });
  }, [presetItems, presetSearch]);

  const selectedPresetItems = useMemo(() => {
    const selected = new Set(selectedPresetKeys);
    return presetItems.filter((p) => selected.has(p.key) && !p.exists);
  }, [presetItems, selectedPresetKeys]);

  const isSearchingOrFiltering = useMemo(() => {
    const q = searchQuery.trim().length > 0;
    const anyFilter = Object.values(columnFilters).some((v) =>
      String(v || "").trim()
    );
    return q || anyFilter;
  }, [searchQuery, columnFilters]);

  const filtered = useMemo(() => {
    let result = [...sources];

    const global = searchQuery.trim().toLowerCase();

    if (global) {
      result = result.filter((s) => {
        const visibleName = translateIfExists(s.name, t, i18n);
        const visibleDescription = translateIfExists(s.description, t, i18n);

        return [s.ui_id, visibleName, visibleDescription].some((f) =>
          normalize(f).includes(global)
        );
      });
    }

    result = result.filter((s) => {
      const visibleName = translateIfExists(s.name, t, i18n);
      const visibleDescription = translateIfExists(s.description, t, i18n);

      if (
        columnFilters.ui_id &&
        !String(s.ui_id ?? "").includes(columnFilters.ui_id.trim())
      ) {
        return false;
      }

      if (
        columnFilters.name &&
        !normalize(visibleName).includes(columnFilters.name.trim().toLowerCase())
      ) {
        return false;
      }

      if (
        columnFilters.description &&
        !normalize(visibleDescription).includes(
          columnFilters.description.trim().toLowerCase()
        )
      ) {
        return false;
      }

      return true;
    });

    return result.sort((a, b) => (a.ui_id ?? 0) - (b.ui_id ?? 0));
  }, [sources, searchQuery, columnFilters, t, i18n]);

  const showingText = useMemo(() => {
    const total = filtered.length;
    return total === 0
      ? t("showing_entries", { start: 0, end: 0, total: 0 })
      : t("showing_entries", { start: 1, end: total, total });
  }, [filtered.length, t]);

  const disableActions = loading || saving || savingOrder;
  const dragDisabled = disableActions || isSearchingOrFiltering;

  const handleColumnFilterChange = (field, value) => {
    setColumnFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleRefreshClick = async () => {
    setSearchQuery("");
    setColumnFilters(defaultColumnFilters);
    setSelectedPresetKeys([]);
    setPresetSearch("");
    setError(null);
    setOpenMenuId(null);
    await fetchSources();
    await fetchTelegramSettings();
  };

  const togglePreset = (key) => {
    const preset = presetItems.find((p) => p.key === key);
    if (!preset || preset.exists) return;

    setSelectedPresetKeys((prev) =>
      prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]
    );
  };

  const addSelectedPresets = async () => {
    if (selectedPresetItems.length === 0) return;

    try {
      setSaving(true);
      setError(null);

      const created = [];

      for (const item of selectedPresetItems) {
        const res = await api.post("/sources", {
          name: getPresetPayloadName(item),
          description: getPresetPayloadDescription(item),
        });

        created.push(res.data);
      }

      setSources((prev) =>
        [...prev, ...created].sort((a, b) => (a.ui_id ?? 0) - (b.ui_id ?? 0))
      );

      setSelectedPresetKeys([]);
      setPresetSearch("");
      setPresetDropdownOpen(false);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.detail || t("sources_error_save"));
      await fetchSources();
    } finally {
      setSaving(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", description: "" });
    setModalOpen(true);
  };

  const openEdit = (src) => {
    if (isLockedSource(src, telegramEnabled)) {
      alert(getLockedReason(src, telegramEnabled, t));
      return;
    }

    setEditing(src);
    setForm({
      name: translateIfExists(src?.name, t, i18n) || "",
      description: translateIfExists(src?.description, t, i18n) || "",
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving || savingOrder) return;
    setModalOpen(false);
    setEditing(null);
  };

  const save = async () => {
    const name = form.name.trim();
    const description = form.description?.trim() || null;

    if (!name) {
      alert(t("source_name_required"));
      return;
    }

    try {
      setSaving(true);
      setError(null);

      if (editing?.id) {
        const res = await api.put(`/sources/${editing.id}`, {
          name,
          description,
        });

        setSources((prev) =>
          prev
            .map((x) => (x.id === editing.id ? res.data : x))
            .sort((a, b) => (a.ui_id ?? 0) - (b.ui_id ?? 0))
        );
      } else {
        const res = await api.post("/sources", { name, description });

        setSources((prev) =>
          [...prev, res.data].sort((a, b) => (a.ui_id ?? 0) - (b.ui_id ?? 0))
        );
      }

      setModalOpen(false);
      setEditing(null);
      setForm({ name: "", description: "" });
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.detail || t("sources_error_save"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (src) => {
    if (isLockedSource(src, telegramEnabled)) {
      alert(getLockedReason(src, telegramEnabled, t));
      return;
    }

    const visibleName = translateIfExists(src.name, t, i18n);
    const label = src.ui_id != null ? `#${src.ui_id}` : visibleName;

    const ok = window.confirm(
      `${t("sources_confirm_delete")}\n\n${label} — ${visibleName}`
    );

    if (!ok) return;

    try {
      setSaving(true);
      setError(null);

      await api.delete(`/sources/${src.id}`);

      setSources((prev) => prev.filter((x) => x.id !== src.id));
      setOpenMenuId(null);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.detail || t("sources_error_delete"));
    } finally {
      setSaving(false);
    }
  };

  const persistUiIds = async (newOrder) => {
    const withNewUi = newOrder.map((s, idx) => ({ ...s, ui_id: idx + 1 }));

    const lockedChanged = withNewUi.some((s) => {
      const prev = sources.find((x) => x.id === s.id);
      return isLockedSource(s, telegramEnabled) && prev && prev.ui_id !== s.ui_id;
    });

    if (lockedChanged) {
      setError(t("sources_system_locked"));
      await fetchSources();
      return;
    }

    const prevUiById = Object.fromEntries(sources.map((s) => [s.id, s.ui_id]));

    const changed = withNewUi.filter(
      (s) => (prevUiById[s.id] ?? null) !== s.ui_id
    );

    if (changed.length === 0) return;

    setSavingOrder(true);
    setError(null);

    try {
      setSources(withNewUi);

      for (const s of changed) {
        await api.put(`/sources/${s.id}`, {
          ui_id: s.ui_id,
          name: s.name,
          description: s.description ?? null,
        });
      }
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.detail || t("sources_error_save_order"));
      await fetchSources();
    } finally {
      setSavingOrder(false);
    }
  };

  const onDragEnd = async (event) => {
    if (isSearchingOrFiltering) return;

    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const activeSource = sources.find((s) => s.id === active.id);
    if (isLockedSource(activeSource, telegramEnabled)) return;

    const list = sources.slice().sort((a, b) => (a.ui_id ?? 0) - (b.ui_id ?? 0));

    const oldIndex = list.findIndex((s) => s.id === active.id);
    const newIndex = list.findIndex((s) => s.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const newOrder = arrayMove(list, oldIndex, newIndex);
    await persistUiIds(newOrder);
  };

  return (
    <div className="container mt-4 sources-page">
      <div className="sources-header">
        <div>
          <h2 className="sources-title">{t("sales_sources")}</h2>
          <div className="sources-subtitle">{t("sources_page_subtitle")}</div>
        </div>

        <div className="sources-header-actions">
          <div className="sources-preset-dropdown" ref={presetDropdownRef}>
            <button
              type="button"
              className="btn btn-outline-primary"
              disabled={disableActions}
              onClick={() => setPresetDropdownOpen((v) => !v)}
            >
              {t("sources_quick_add_title")}
              {selectedPresetKeys.length > 0 && (
                <span className="sources-selected-count">
                  {selectedPresetKeys.length}
                </span>
              )}
            </button>

            {presetDropdownOpen && (
              <div className="sources-preset-menu">
                <div className="sources-preset-menu-head">
                  <strong>{t("sources_quick_add_title")}</strong>
                  <small>{t("sources_quick_add_subtitle")}</small>
                </div>

                <input
                  className="form-control form-control-sm sources-preset-search"
                  placeholder={t("search")}
                  value={presetSearch}
                  onChange={(e) => setPresetSearch(e.target.value)}
                  autoFocus
                />

                <div className="sources-preset-list">
                  {availablePresetItems.length === 0 && (
                    <div className="sources-preset-empty">
                      {t("sources_empty")}
                    </div>
                  )}

                  {availablePresetItems.map((preset) => {
                    const selected = selectedPresetKeys.includes(preset.key);

                    return (
                      <label
                        key={preset.key}
                        className={`sources-preset-option ${
                          selected ? "is-selected" : ""
                        } ${preset.exists ? "is-disabled" : ""}`}
                        title={
                          preset.exists
                            ? `${preset.name} — ${tOr(t, "already_added", "Already added")}`
                            : preset.description
                        }
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          disabled={preset.exists}
                          onChange={() => togglePreset(preset.key)}
                        />

                        <span>{preset.name}</span>
                        {preset.exists && (
                          <small className="sources-preset-added">
                            {tOr(t, "already_added", "Already added")}
                          </small>
                        )}
                      </label>
                    );
                  })}
                </div>

                <div className="sources-preset-menu-footer">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => setSelectedPresetKeys([])}
                    disabled={selectedPresetKeys.length === 0}
                  >
                    {t("clear")}
                  </button>

                  <button
                    type="button"
                    className="btn btn-sm btn-success"
                    onClick={addSelectedPresets}
                    disabled={selectedPresetItems.length === 0 || saving}
                  >
                    {saving
                      ? `${t("saving")}...`
                      : t("sources_add_selected", {
                          count: selectedPresetItems.length,
                        })}
                  </button>
                </div>
              </div>
            )}
          </div>

          <button
            className="btn btn-outline-success"
            onClick={openCreate}
            disabled={disableActions}
          >
            {t("create")}
          </button>

          <button
            className="btn btn-outline-secondary"
            onClick={handleRefreshClick}
            disabled={disableActions}
          >
            {loading ? `${t("loading")}...` : t("refresh")}
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}

      <div className="sources-table-card">
        <div className="sources-table-toolbar">
          <div className="sources-table-info">
            <span>{showingText}</span>

            {isSearchingOrFiltering && (
              <span className="badge bg-info text-dark">
                {t("sources_reorder_disabled_while_searching")}
              </span>
            )}

            {savingOrder && (
              <span className="badge bg-warning text-dark">
                {t("saving")}...
              </span>
            )}
          </div>

          <div className="input-group input-group-sm sources-search">
            <input
              className="form-control"
              placeholder={t("search")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              disabled={disableActions}
            />

            <button
              className="btn btn-outline-secondary"
              type="button"
              title={t("reset")}
              onClick={() => setSearchQuery("")}
              disabled={!searchQuery || disableActions}
            >
              ✕
            </button>
          </div>
        </div>

        <div className="sources-mobile-list">
          {filtered.length === 0 && !loading && (
            <div className="sources-mobile-empty">
              {t("sources_empty")}
            </div>
          )}

          {filtered.map((src) => {
            const visibleName = translateIfExists(src.name, t, i18n);
            const visibleDescription =
              translateIfExists(src.description, t, i18n) || "-";

            const sourceLocked = isLockedSource(src, telegramEnabled);
            const sourceLockedReason = getLockedReason(src, telegramEnabled, t);

            return (
              <article
                className={`sources-mobile-card ${sourceLocked ? "is-locked" : ""}`}
                key={src.id}
                title={sourceLocked ? sourceLockedReason : undefined}
              >
                <div className="sources-mobile-card-top">
                  <div className="sources-mobile-title-wrap">
                    <span className="sources-mobile-id">
                      #{src.ui_id ?? "-"}
                    </span>

                    <h3>{visibleName || "-"}</h3>
                  </div>

                  <button
                    type="button"
                    className="sources-mobile-drag"
                    title={
                      sourceLocked
                        ? sourceLockedReason
                        : dragDisabled
                        ? isSearchingOrFiltering
                          ? t("sources_clear_search_filters_to_reorder")
                          : t("sources_reorder_disabled")
                        : t("sources_drag_to_reorder")
                    }
                    disabled={dragDisabled || sourceLocked}
                  >
                    ☰
                  </button>
                </div>

                <div className="sources-mobile-description">
                  {visibleDescription}
                </div>

                <div className="sources-mobile-actions">
                  <button
                    type="button"
                    className="btn btn-warning btn-sm"
                    disabled={disableActions || sourceLocked}
                    title={sourceLocked ? sourceLockedReason : undefined}
                    onClick={() => openEdit(src)}
                  >
                    <i className="lni lni-pencil-1"></i>
                    <span>{t("edit")}</span>
                  </button>

                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm"
                    disabled={disableActions || sourceLocked}
                    title={sourceLocked ? sourceLockedReason : undefined}
                    onClick={() => remove(src)}
                  >
                    <i className="lni lni-trash-3"></i>
                    <span>{t("delete")}</span>
                  </button>
                </div>
              </article>
            );
          })}

          {loading && (
            <div className="sources-mobile-empty">
              {t("loading")}...
            </div>
          )}
        </div>

        <div className="table-responsive">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onDragEnd}
          >
            <SortableContext
              items={filtered.map((s) => s.id)}
              strategy={verticalListSortingStrategy}
            >
              <table className="table table-striped table-hover align-middle sources-table">
                <thead>
                  <tr>
                    <th className="sources-col-drag" />
                    <th className="sources-col-id">{t("id")}</th>
                    <th>{t("source_name")}</th>
                    <th>{t("description")}</th>
                    <th className="sources-col-actions">{t("actions")}</th>
                  </tr>

                  <tr>
                    <th />
                    <th>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder={t("id")}
                        value={columnFilters.ui_id}
                        onChange={(e) =>
                          handleColumnFilterChange("ui_id", e.target.value)
                        }
                        disabled={disableActions}
                      />
                    </th>

                    <th>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder={t("source_name")}
                        value={columnFilters.name}
                        onChange={(e) =>
                          handleColumnFilterChange("name", e.target.value)
                        }
                        disabled={disableActions}
                      />
                    </th>

                    <th>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder={t("description")}
                        value={columnFilters.description}
                        onChange={(e) =>
                          handleColumnFilterChange("description", e.target.value)
                        }
                        disabled={disableActions}
                      />
                    </th>

                    <th />
                  </tr>
                </thead>

                <tbody>
                  {filtered.length === 0 && !loading && (
                    <tr>
                      <td colSpan={5} className="text-center py-4">
                        {t("sources_empty")}
                      </td>
                    </tr>
                  )}

                  {filtered.map((src) => (
                    <SourcesRow
                      key={src.id}
                      src={src}
                      onEdit={openEdit}
                      onDelete={remove}
                      disabled={disableActions}
                      dragDisabled={dragDisabled}
                      isSearchingOrFiltering={isSearchingOrFiltering}
                      openMenuId={openMenuId}
                      setOpenMenuId={setOpenMenuId}
                      openMenuRef={openMenuRef}
                      saving={saving || savingOrder}
                      isLocked={isLockedSource(src, telegramEnabled)}
                      lockedReason={getLockedReason(src, telegramEnabled, t)}
                      t={t}
                      i18n={i18n}
                    />
                  ))}

                  {loading && (
                    <tr>
                      <td colSpan={5} className="text-center py-4">
                        {t("loading")}...
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </SortableContext>
          </DndContext>
        </div>
      </div>

      {modalOpen && (
        <div className="modal show sources-modal-backdrop">
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content bg-dark text-light">
              <div className="modal-header border-secondary">
                <h5 className="modal-title">
                  {editing ? t("edit_source") : t("create_source")}
                </h5>

                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={closeModal}
                />
              </div>

              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">{t("source_name")}</label>
                  <input
                    className="form-control"
                    value={form.name}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, name: e.target.value }))
                    }
                    disabled={saving || savingOrder}
                    autoFocus
                  />
                </div>

                <div className="mb-2">
                  <label className="form-label">{t("description")}</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={form.description}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, description: e.target.value }))
                    }
                    disabled={saving || savingOrder}
                  />
                </div>
              </div>

              <div className="modal-footer border-secondary">
                <button
                  className="btn btn-outline-secondary"
                  onClick={closeModal}
                  disabled={saving || savingOrder}
                >
                  {t("cancel")}
                </button>

                <button
                  className="btn btn-primary"
                  onClick={save}
                  disabled={saving || savingOrder}
                >
                  {saving ? `${t("saving")}...` : t("save")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SourcesPage;